// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SortDirection } from '@/constants/sort';
import { getItems } from '@/items/api/get-items';
import { ItemSortField, ItemsFilter } from '@/items/api/types';
import { ItemTypeFilter } from '@/items/constants/item-types';
import { ONE_MINUTE_MS, TWO_MINUTES_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Query key for items.
 */
export const itemsQueryKey = ['items'] as const;

/**
 * Options for the useItems hook.
 */
interface UseItemsOptions {
  type?: ItemTypeFilter;
  search?: string;
  sort?: ItemSortField;
  direction?: SortDirection;
  page?: number;
  limit?: number;
  archived?: boolean;
}

/**
 * React hook for fetching and managing items data.
 * Uses React Query with suspense mode for data fetching.
 */
export function useItems(options: UseItemsOptions = {}) {
  const filter: ItemsFilter = {
    type: options.type,
    search: options.search,
    sort: options.sort,
    direction: options.direction,
    page: options.page,
    limit: options.limit,
    archived: options.archived,
  };

  const { data, ...query } = useSuspenseQuery({
    queryKey: [...itemsQueryKey, filter],
    queryFn: () => getItems(filter),
    refetchOnWindowFocus: true,
    staleTime: ONE_MINUTE_MS,
    refetchInterval: TWO_MINUTES_MS,
  });

  return {
    items: data.results,
    total: data.totalCount,
    ...query,
  };
}
